import { useState } from "react";
import { toast } from "sonner";
import { LifeBuoy, Mail, Clock, Send, Instagram } from "lucide-react";
import { Reveal, SectionHeading } from "./Reveal";

export const AppContact = () => {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email, and message.");
      return;
    }
    toast.success("Message sent — our team will get back to you shortly.", {
      description: "Thanks for reaching out to Taurus Technology.",
    });
    setForm({ name: "", email: "", subject: "", message: "" });
  };

  return (
    <section id="contact" className="section-pad relative overflow-hidden bg-tt-gunmetal/30" data-testid="contact-section">
      <div className="pointer-events-none absolute left-0 top-1/3 h-96 w-96 rounded-full bg-tt-gold/5 blur-[130px]" />
      <img
        src="/assets/horns_wire_t.png"
        alt=""
        aria-hidden="true"
        className="pointer-events-none absolute right-[-10%] top-1/2 w-[680px] max-w-none -translate-y-1/2 opacity-[0.11]"
      />
      <div className="relative mx-auto max-w-7xl px-6">
        <SectionHeading
          eyebrow="We're Here To Help"
          title="Support & Contact"
          subtitle="Questions about an app, a bug on stage, or a partnership idea? Reach out — performers get priority."
        />

        <div className="mt-16 grid gap-8 lg:grid-cols-5">
          {/* Info / support */}
          <Reveal className="lg:col-span-2">
            <div className="flex h-full flex-col gap-4">
              <div className="rounded-2xl border border-tt-gold/20 bg-tt-gold/5 p-6">
                <div className="flex items-center gap-3">
                  <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-tt-gold text-tt-black">
                    <LifeBuoy size={20} />
                  </span>
                  <div>
                    <p className="font-display text-base font-semibold text-tt-chrome">App Support</p>
                    <p className="text-xs text-tt-silver/55">Fast help for live issues</p>
                  </div>
                </div>
                <a href="mailto:support@taurustechapps.com"
                  className="mt-4 block font-grotesk text-sm text-tt-gold hover:text-tt-goldsoft"
                  data-testid="support-email">
                  support@taurustechapps.com
                </a>
              </div>

              <InfoCard icon={Mail} title="General Inquiries" lines={["support@taurustechapps.com"]} href="mailto:support@taurustechapps.com" testid="contact-email-link" />
              <InfoCard icon={Clock} title="Support Hours" lines={["Mon – Fri: 9:00 AM – 6:00 PM", "Show nights: priority response"]} />

              <div className="rounded-2xl border border-white/5 bg-tt-black/40 p-6">
                <p className="font-grotesk text-xs uppercase tracking-[0.2em] text-tt-silver/45">Follow</p>
                <div className="mt-4 flex gap-3">
                  <SocialBtn icon={Instagram} label="Instagram" href="https://instagram.com/TTechapps" testid="social-instagram" />
                  <SocialBtn icon={TikTokIcon} label="TikTok" href="https://tiktok.com/@taurustechapps" testid="social-tiktok" />
                </div>
              </div>
            </div>
          </Reveal>

          {/* Form */}
          <Reveal delay={0.1} className="lg:col-span-3">
            <form onSubmit={submit} className="rounded-3xl metal-surface border border-white/5 p-8" data-testid="contact-form">
              <div className="grid gap-5 sm:grid-cols-2">
                <Field label="Full Name *">
                  <input value={form.name} onChange={set("name")} data-testid="contact-name" className="tt-input" placeholder="Your name" />
                </Field>
                <Field label="Email *">
                  <input type="email" value={form.email} onChange={set("email")} data-testid="contact-email" className="tt-input" placeholder="you@email.com" />
                </Field>
              </div>
              <Field label="Subject" className="mt-5">
                <select value={form.subject} onChange={set("subject")} data-testid="contact-subject" className="tt-input">
                  <option value="">Select a topic</option>
                  <option>App Support / Bug Report</option>
                  <option>Feature Request</option>
                  <option>Billing / App Store</option>
                  <option>Partnership / Press</option>
                  <option>Something Else</option>
                </select>
              </Field>
              <Field label="Message *" className="mt-5">
                <textarea value={form.message} onChange={set("message")} rows={6} data-testid="contact-message"
                  className="tt-input resize-none" placeholder="How can we help?" />
              </Field>
              <button type="submit"
                className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-tt-gold py-3.5 font-grotesk text-sm font-semibold text-tt-black transition-all hover:bg-tt-goldsoft hover:shadow-[0_0_30px_-6px_rgba(200,162,90,0.6)]"
                data-testid="contact-submit">
                <Send size={16} /> Send Message
              </button>
            </form>
          </Reveal>
        </div>
      </div>

      <style>{`
        .tt-input {
          width: 100%;
          border-radius: 0.75rem;
          border: 1px solid rgba(255,255,255,0.08);
          background: rgba(5,5,5,0.6);
          padding: 0.75rem 1rem;
          font-size: 0.875rem;
          color: #F5F5F5;
          font-family: 'Inter', sans-serif;
          transition: border-color 0.3s ease, box-shadow 0.3s ease;
          outline: none;
        }
        .tt-input::placeholder { color: rgba(201,205,210,0.35); }
        .tt-input:focus { border-color: rgba(200,162,90,0.6); box-shadow: 0 0 0 3px rgba(200,162,90,0.12); }
        .tt-input option { background: #131316; }
      `}</style>
    </section>
  );
};

const Field = ({ label, children, className = "" }) => (
  <label className={`block ${className}`}>
    <span className="mb-2 block font-grotesk text-xs uppercase tracking-wider text-tt-silver/50">{label}</span>
    {children}
  </label>
);

const InfoCard = ({ icon: Icon, title, lines, href, testid }) => (
  <div className="flex items-start gap-4 rounded-2xl border border-white/5 bg-tt-black/40 p-5">
    <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-tt-gold/10 text-tt-gold">
      <Icon size={18} />
    </span>
    <div>
      <p className="font-display text-sm font-semibold text-tt-chrome">{title}</p>
      {lines.map((l, i) =>
        href && i === 0 ? (
          <a key={l} href={href} className="text-xs text-tt-gold hover:text-tt-goldsoft" data-testid={testid}>{l}</a>
        ) : (
          <p key={l} className="text-xs text-tt-silver/55">{l}</p>
        )
      )}
    </div>
  </div>
);

const TikTokIcon = ({ size = 18 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor" aria-hidden="true">
    <path d="M16.6 5.82a4.28 4.28 0 0 1-1.05-2.82h-3.1v12.46a2.59 2.59 0 1 1-2.59-2.59c.27 0 .53.04.78.12V9.83a5.69 5.69 0 0 0-.78-.05 5.69 5.69 0 1 0 5.69 5.69V9.01a7.34 7.34 0 0 0 4.29 1.37V7.28a4.29 4.29 0 0 1-3.24-1.46z" />
  </svg>
);

const SocialBtn = ({ icon: Icon, label, href = "#", testid }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    className="flex h-11 w-11 items-center justify-center rounded-lg border border-white/10 text-tt-silver/70 transition-all hover:border-tt-gold/50 hover:text-tt-gold"
    aria-label={label}
    data-testid={testid}
  >
    <Icon size={18} />
  </a>
);
